/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Text, Typography } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';
import React, { useEffect } from 'react';

import { ENoteStatus } from '@blend-types/eclose';
import { useSelector } from 'react-redux';
import { enterpriseColors } from '../../../../style/colors';
import { SignAheadDocsStatus } from '../../../../shared/constants/room';
import { State } from '../../../../redux';
import { NotaryAPI } from '../../../../api/notary/types';
import { useNotaryTasks } from '../../../../hooks/useNotaryTasks';
import { appcuesEvents } from '../../../../api/appcues/events';
import { NotaryTasksCard } from './NotaryTasksCard';
import { isNil } from '@utils/lodash';

const styles = StyleSheet.create({
  wrapper: {
    ...Typography.Roboto(),
    color: enterpriseColors.NEUTRAL_800,
    padding: '20px 20px',
    maxWidth: 600,
    border: `1px solid ${enterpriseColors.NEUTRAL_200}`,
    borderRadius: 6,
    backgroundColor: enterpriseColors.WHITE,
    alignItems: 'start',
  },

  statusCard: {
    color: enterpriseColors.NEUTRAL_700,
    margin: 7,
    fontSize: 14,
  },
});

const ENoteInRONStatusMapping: Record<ENoteStatus, string> = {
  COMPLETE: 'Signed inside Signing Room',
  INVALID: 'Invalid eNote inside Signing Room (Potential Issue)',
  READY_TO_SIGN: 'Inside Signing Room (Unsigned)',
  SIGNER_WITHDRAWN: 'Signer Withdrawn (Potential Issue)',
};

const ENoteOutsideOfRONStatusMapping: Record<ENoteStatus, string> = {
  COMPLETE: 'Signed',
  INVALID: 'Invalid eNote (Potential Issue)',
  READY_TO_SIGN: 'Pending Borrower Signature',
  SIGNER_WITHDRAWN: 'Signer Withdrawn (Potential Issue)',
};

const SignAheadDocsInRONStatusMapping: Record<SignAheadDocsStatus, string> = {
  SIGNED: 'Signed inside Signing Room',
  NOT_AVAILABLE: 'Not Available',
  PENDING_BORROWER_SIGNATURE: 'Inside Signing Room (Unsigned)',
};

const SignAheadDocsOutsideOfRONStatusMapping: Record<SignAheadDocsStatus, string> = {
  PENDING_BORROWER_SIGNATURE: 'Pending Borrower Signature',
  SIGNED: 'Signed',
  NOT_AVAILABLE: 'Not Available',
};

export namespace DocsStatusCard {
  export interface Props {
    roomId: string;
  }
}

const containsPendingOrUnsigned = (arr: string[]) =>
  arr.some(str => str.includes('Pending') || str.includes('Unsigned'));

export const DocsStatusCard: React.FC<DocsStatusCard.Props> = ({ roomId }) => {
  const { isLoading: isNotaryTasksLoading, dispatchNotaryTasks } = useNotaryTasks(roomId);
  const roomDetail = useSelector((reduxState: State) => reduxState.roomDetail);
  const metadata = roomDetail.metadata as NotaryAPI.RoomMetadata;
  const notaryTasks = roomDetail.notaryTasks;
  let eNoteStatusDescription: string;

  const doNotaryTasksExist = notaryTasks && notaryTasks.length > 0;
  const shouldRenderNotaryTasks = !isNotaryTasksLoading && doNotaryTasksExist;

  if (isNil(metadata) || isNil(metadata.eNoteStatus) || isNil(metadata.signENoteInRON)) {
    eNoteStatusDescription = 'Not Available';
  } else {
    eNoteStatusDescription = metadata.signENoteInRON
      ? ENoteInRONStatusMapping[metadata.eNoteStatus]
      : ENoteOutsideOfRONStatusMapping[metadata.eNoteStatus];
  }
  let signAheadDocsStatusDescription: string;
  if (isNil(metadata) || isNil(metadata.signAheadDocsStatus) || isNil(metadata.sentSignAheadDocsToRON)) {
    signAheadDocsStatusDescription = 'Not Available';
  } else {
    signAheadDocsStatusDescription = metadata.sentSignAheadDocsToRON
      ? SignAheadDocsInRONStatusMapping[metadata.signAheadDocsStatus]
      : SignAheadDocsOutsideOfRONStatusMapping[metadata.signAheadDocsStatus];
  }

  useEffect(() => {
    if (containsPendingOrUnsigned([signAheadDocsStatusDescription, eNoteStatusDescription])) {
      appcuesEvents.highlightPendingESignOrPromissoryNoteCard({ roomId });
    }
  }, [signAheadDocsStatusDescription, eNoteStatusDescription]);

  useEffect(() => {
    (async () => await dispatchNotaryTasks())();
  }, [roomId]);

  return (
    <div>
      <div className={css(styles.wrapper)} data-it="DocsStatusCard">
        <div data-it="eSignStatusDescription" className={css(styles.statusCard)}>
          <Text weight="semibold">eSign Documents: </Text>
          <Text>{signAheadDocsStatusDescription}</Text>
        </div>
        <div data-it="eNoteStatusDescription" className={css(styles.statusCard)}>
          <Text weight="semibold">Promissory Note: </Text>
          <Text>{eNoteStatusDescription}</Text>
        </div>
      </div>
      {shouldRenderNotaryTasks && <NotaryTasksCard roomId={roomId} />}
    </div>
  );
};
